import { useState } from "react";
import { Alert, Button, SectionCard, TextArea } from "@/deps/ui-library";
import { useActOnApproval, useDocumentApproval, useSubmitForApproval } from "@/api/queries";
import type { ApprovalDocumentKind, DocumentApprovalRequest, Id } from "@/api/types";
import { useApprovalReview } from "@/components/ApprovalReview";
import { Dialog } from "@/components/Dialog";
import { formatDate } from "@/lib/dates";

/** A request that's still going: the document can't be changed while it is. */
export const isLive = (request: DocumentApprovalRequest | null | undefined) =>
  request != null && request.status !== "returned";

/**
 * What a draft can do next, which follows the approval rule. With no approval needed it's Issue and
 * post, as it always was. With one needed, it's Submit, then the step this person can take, and Issue
 * and post once approved.
 */
export function ApprovalActions({
  kind,
  id,
  canCreate,
  canIssue,
  issuing,
  onIssue,
  issueLabel = "Issue and post",
  approveLabel = "Approve and issue",
  workingLabel = "Issuing",
}: {
  kind: ApprovalDocumentKind;
  id: Id;
  canCreate: boolean;
  canIssue: boolean;
  issuing: boolean;
  onIssue: () => void;
  /** What finishing it is called, which differs by kind of document. */
  issueLabel?: string;
  approveLabel?: string;
  workingLabel?: string;
}) {
  const { data: approval } = useDocumentApproval(kind, id);
  const submit = useSubmitForApproval();
  const act = useActOnApproval();
  const review = useApprovalReview();
  const [returning, setReturning] = useState(false);
  const [reason, setReason] = useState("");

  if (!approval) return null;

  const request = approval.request;
  const live = isLive(request);
  const actId = request ? `request:${kind}:${request.id}:${id}` : "";
  const failure = submit.error ?? (returning ? null : act.error);

  const issueButton = (
    <Button colorScheme="primary" variant="solid" size="sm" disabled={issuing} onClick={onIssue}>
      {issuing ? workingLabel : issueLabel}
    </Button>
  );

  return (
    <>
      {failure ? (
        <span role="alert" className="text-bad-text text-xs">
          {(failure as Error).message}
        </span>
      ) : null}

      {!live && !approval.required && canIssue ? issueButton : null}

      {!live && approval.required && canCreate ? (
        <Button
          colorScheme="primary"
          variant="solid"
          size="sm"
          disabled={submit.isPending}
          onClick={() => submit.mutate({ kind, id })}
        >
          {submit.isPending ? "Submitting" : "Submit for approval"}
        </Button>
      ) : null}

      {request?.status === "approved" && canIssue ? issueButton : null}

      {live && request?.step && !request.mayAct ? (
        <span className="text-fg-muted text-xs">
          {request.step === "Verify" ? "Waiting to be verified" : "Waiting for approval"}
        </span>
      ) : null}

      {live && request?.step && request.mayAct ? (
        <>
          <Button
            colorScheme="default"
            variant="outline"
            size="sm"
            disabled={act.isPending}
            onClick={() => setReturning(true)}
          >
            Return
          </Button>
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={act.isPending}
            onClick={() => {
              const action = request.step === "Verify" ? "verify" : "approve";
              act.mutate({ id: actId, action }, { onSuccess: () => review?.(action) });
            }}
          >
            {act.isPending ? "Working" : request.step === "Verify" ? "Verify" : approveLabel}
          </Button>
        </>
      ) : null}

      <Dialog
        open={returning}
        onClose={() => setReturning(false)}
        title="Return to preparer"
        description="They can change it and submit it again. The reason stays on the document's history."
        footer={
          <Button
            colorScheme="primary"
            variant="solid"
            size="sm"
            disabled={reason.trim() === "" || act.isPending}
            onClick={() =>
              act.mutate(
                { id: actId, action: "return", note: reason },
                {
                  onSuccess: () => {
                    setReturning(false);
                    setReason("");
                    review?.("return");
                  },
                },
              )
            }
          >
            {act.isPending ? "Returning" : "Return"}
          </Button>
        }
      >
        <div className="flex flex-col gap-3">
          {act.isError ? (
            <Alert
              config={{ variant: "subtle", colorScheme: "danger" }}
              description={(act.error as Error).message}
            />
          ) : null}
          <TextArea
            label="Reason"
            required
            rows={3}
            value={reason}
            onChange={(event) => setReason(event.target.value)}
          />
        </div>
      </Dialog>
    </>
  );
}

/** The steps a document has been through. Nothing until it has been submitted. */
export function ApprovalCard({ kind, id }: { kind: ApprovalDocumentKind; id: Id }) {
  const { data: approval } = useDocumentApproval(kind, id);
  const request = approval?.request;
  if (!request) return null;

  const when = (at: string | null | undefined) => (at ? formatDate(at.slice(0, 10)) : "");
  const steps: Array<{ label: string; by?: string | null; at?: string | null }> = [
    { label: "Submitted", by: request.submittedBy, at: request.submittedAt },
  ];
  if (request.levels === 2 && request.verifiedAt) {
    steps.push({ label: "Verified", by: request.verifiedBy, at: request.verifiedAt });
  }
  if (request.approvedAt)
    steps.push({ label: "Approved", by: request.approvedBy, at: request.approvedAt });
  if (request.returnedAt)
    steps.push({ label: "Returned", by: request.returnedBy, at: request.returnedAt });

  return (
    <SectionCard title="Approval">
      <ul className="flex flex-col gap-1.5 text-xs">
        {steps.map((step) => (
          <li key={step.label} className="flex flex-wrap justify-between gap-x-2">
            <span className="text-fg">{step.label}</span>
            <span className="text-fg-muted">
              {[step.by, when(step.at)].filter(Boolean).join(" · ")}
            </span>
          </li>
        ))}
        {request.returnReason ? (
          <li className="text-bad-text whitespace-pre-line">{request.returnReason}</li>
        ) : null}
      </ul>
    </SectionCard>
  );
}

/** Whether a draft can still be edited or deleted, for a page to hide those while it's with the approvers. */
export function useApprovalHoldsDraft(
  kind: ApprovalDocumentKind,
  id: Id,
  isDraft: boolean,
): boolean {
  const { data } = useDocumentApproval(kind, id, isDraft);
  // The approval card reads the same query whatever this asks, so the answer has to check too. An
  // issued document still has its approved request, and nothing is holding it.
  return isDraft && isLive(data?.request);
}
